/* UTILS */
import { computed, ref } from "vue"
import { useCookies } from "@vueuse/integrations/useCookies"
import { COOKIES } from "@/config/constants"

/* STORES */
import { useUserStore } from "@/stores/user"
import { storeToRefs } from "pinia"
import { useI18nStore } from "@/stores/i18n"
import { useConfigStore } from "@/stores/config"

/* API */
import { patchConsent } from "@/api/viktor/me/me"
import { refreshToken } from "@/api/viktor/authentication/login"
import { logout } from "@/api/viktor/authentication"

export const usePendingUser = () => {
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)
  const { fetchUser } = userStore
  const isConsentLoading = ref(false)

  const cookies = useCookies()
  const { currentLocale } = storeToRefs(useI18nStore())
  const { updateLocale } = useI18nStore()

  const isConsentNeeded = computed(
    () => !!user.value?.permissions?.includes("cst_na")
  )

  const patchUserConsent = async () => {
    await patchConsent(user.value.user.uuid)
  }

  const updateUserLocale = async () => {
    // cst_na user cannot update his locale
    // We update the app locale, accept the cst and then update the user locale
    const appLocaleId = currentLocale.value.id
    if (user.value.user.locale_id !== appLocaleId) {
      await updateLocale(appLocaleId)
    }
  }

  const consentAndRefreshUser = async () => {
    if (isConsentLoading.value) return
    isConsentLoading.value = true

    try {
      await patchUserConsent()
      await refreshToken(cookies.get(COOKIES.REFRESH_TOKEN))
      await updateUserLocale()
      await fetchUser()
    } finally {
      isConsentLoading.value = false
    }
  }

  const logoutAndRedirectToAuth = (email: string) => {
    const cookies = useCookies()
    const authUrl = useConfigStore().urls.auth_url

    const encodedEmail = encodeURIComponent(email)
    const registerAuthUrl = `${authUrl}/register/personal?from=screening&email=${encodedEmail}`

    // Set cookie to keep the workflow and call next assessment after register
    cookies.set(COOKIES.GOTO_NEXT, 1, COOKIES.OPTIONS)

    // Clear all cookies
    const cookiesToClear = [
      COOKIES.PM_UUID,
      COOKIES.COMPANY_UUID,
      COOKIES.REFRESH_TOKEN,
      COOKIES.ACCESS_TOKEN,
    ]
    cookiesToClear.forEach((cookie) => cookies.remove(cookie, COOKIES.OPTIONS))

    logout()
    window.location.href = registerAuthUrl
  }

  return {
    isConsentNeeded,
    consentAndRefreshUser,
    isConsentLoading,
    logoutAndRedirectToAuth,
  }
}
